import { useEffect, useRef, useState } from "react";
import { ExternalLink, Megaphone } from "lucide-react";
import { ads, type AdCampaign } from "@/domains/ads";

/**
 * Renders live, admin-approved ad campaigns as sponsored cards.
 * Logs an impression once per session per campaign and a click on tap.
 */
export const SponsoredAdsRail = () => {
  const [items, setItems] = useState<AdCampaign[]>([]);
  const loggedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    ads.listLiveForFeed(5).then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    items.forEach((c) => {
      if (loggedRef.current.has(c.id)) return;
      loggedRef.current.add(c.id);
      ads.logEvent(c.id, "impression").catch(() => {});
    });
  }, [items]);

  if (items.length === 0) return null;

  const handleClick = (c: AdCampaign) => {
    ads.logEvent(c.id, "click").catch(() => {});
    if (c.cta_url) {
      const url = c.cta_url.startsWith("http") ? c.cta_url : `https://${c.cta_url}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section className="px-5 mt-4">
      <div className="flex items-center gap-2 mb-2">
        <Megaphone className="h-4 w-4 text-primary" />
        <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Sponsored
        </h2>
      </div>
      <div className="flex gap-3 overflow-x-auto -mx-1 px-1 pb-2 snap-x snap-mandatory">
        {items.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => handleClick(c)}
            className="snap-start shrink-0 w-[78%] text-left rounded-2xl border border-border bg-card overflow-hidden hover:border-primary transition-colors"
          >
            {c.image_url ? (
              <div className="h-28 w-full bg-muted overflow-hidden">
                <img
                  src={c.image_url}
                  alt={c.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="h-28 w-full bg-gradient-to-br from-primary/15 to-accent/15" />
            )}
            <div className="p-3 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <p className="font-display font-bold text-sm truncate">{c.title}</p>
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">
                  Ad
                </span>
              </div>
              {c.body && (
                <p className="text-xs text-muted-foreground line-clamp-2">{c.body}</p>
              )}
              {c.cta_label && (
                <p className="text-xs font-semibold text-primary inline-flex items-center gap-1 pt-1">
                  {c.cta_label} <ExternalLink className="h-3 w-3" />
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};
